// /events: the plaza's calendar (pitch nights, concerts, meetups) in the viewer's time zone, "Join in Threedom",
// scheduling for signed-in 18+ accounts, and featuring for moderators. Rules: src/events.ts and docs/backend/rooms.md.

import { api, ApiError } from "../api.ts";
import { clear, errorBox, h, link, toast } from "../dom.ts";
import {
  checkDraft,
  eventError,
  featureEvent,
  fmtTime,
  groupByDay,
  joinLink,
  KIND_BLURB,
  KIND_LABEL,
  loadCalendar,
  parseLocal,
  PLAZA_GAME,
  scheduleEvent,
  sizeText,
  toLocalInput,
  viewerZone,
  type Calendar,
  type Draft,
  type EventKind,
  type PlazaEvent,
} from "../events.ts";
import { downloadPath, launch } from "../play.ts";
import { hauntEventCards } from "../zones/open-haunt/portal.ts";
import { navigate, setTitle } from "../router.ts";
import { currentMe, nameOf, signedIn, type Me } from "../session.ts";

type Filter = "all" | EventKind;
const FILTERS: [Filter, string][] = [
  ["all", "All"],
  ["pitch", "Pitch nights"],
  ["concert", "Concerts"],
  ["meetup", "Meetups"],
];

/**
 * Is this viewer a moderator? Rooms' HTTP doesn't say, so the page asks community's unscoped moderation log (moderators
 * only; creators read it only with `?game=`) and shows Feature; rooms still decides when the button is used.
 */
let moderatorHint: { id: string; ok: Promise<boolean> } | null = null;
function isModerator(): Promise<boolean> {
  const me = currentMe();
  if (!signedIn() || !me) return Promise.resolve(false);
  // per user: signing out and in as someone else doesn't reload the page
  if (moderatorHint?.id !== me.id) moderatorHint = { id: me.id, ok: api("community", "/moderation/log?limit=1").then(() => true, () => false) };
  return moderatorHint.ok;
}

export function eventsPage(_p: unknown, query: URLSearchParams): HTMLElement {
  setTitle("Events");
  const me = currentMe();
  let filter: Filter = (FILTERS.find(([f]) => f === query.get("kind"))?.[0] ?? "all") as Filter;
  let cal: Calendar | null = null;
  const pills = h("div", { class: "filter-row", role: "group", "aria-label": "Kind of event" });
  const list = h("div", { class: "events-list", "aria-live": "polite" }, h("p", { class: "dim" }, "Loading…"));
  const hostBox = h("div");
  const zone = h("p", { class: "dim small" }, `Times are in your time zone (${viewerZone().replace(/_/g, " ")}).`);

  const draw = async () => {
    clear(
      pills,
      FILTERS.map(([f, text]) =>
        h(
          "button",
          {
            type: "button",
            class: `pill${f === filter ? " on" : ""}`,
            "aria-pressed": f === filter ? "true" : "false",
            onclick: () => {
              filter = f;
              history.replaceState(null, "", f === "all" ? "/events" : `/events?kind=${f}`);
              void draw();
            },
          },
          text,
        ),
      ),
    );
    if (!cal) return;
    const mod = await isModerator();
    const shown = cal.events.filter((e) => filter === "all" || e.kind === filter);
    // nothing scheduled (of this kind): no filter pills and no "nothing here" panel, just what's on anyway
    pills.hidden = zone.hidden = !cal.events.length;
    if (!shown.length) {
      clear(list, filter === "all" ? null : h("p", { class: "dim" }, `No ${FILTERS.find(([f]) => f === filter)![1].toLowerCase()} coming up.`), onAnyway());
      return;
    }
    clear(
      list,
      groupByDay(shown).map((g) =>
        h("section", { class: "event-day" }, h("h2", null, g.label), h("ul", { class: "events" }, g.events.map((e) => eventCard(e, me, mod, reload)))),
      ),
    );
  };

  const reload = async () => {
    try {
      cal = await loadCalendar();
    } catch (e) {
      // the calendar can't be read: show what's on anyway rather than an error on a player page
      pills.hidden = zone.hidden = true;
      clear(list, e instanceof ApiError && (e.code === "service_unavailable" || e.status >= 502) ? onAnyway() : errorBox(e));
      return;
    }
    await draw();
    clear(hostBox, hostPanel(me, reload));
  };
  void draw();
  void reload();

  return h(
    "div",
    { class: "events-page" },
    h("h1", null, "Events"),
    h(
      "p",
      { class: "lead" },
      "Pitch nights, concerts and meetups in the plaza, where you show up as your avatar. Join from the Banger app and you're in the crowd.",
    ),
    zone,
    hauntEventCards(), // Open Haunt and its 7 Nov Plaza night, with countdowns (w12-haunt; null after 10 Nov)
    pills,
    list,
    hostBox,
  );
}

/** The plaza, which is always open: shown when nothing is scheduled (Open Haunt's cards sit above, from its zone). */
function onAnyway(): HTMLElement {
  return h(
    "div",
    { class: "on-anyway" },
    h(
      "div",
      { class: "panel" },
      h("h2", null, "The plaza is always open"),
      h("p", null, "Drop in, meet people, and jump into games through the portals."),
      joinButton("1", "Visit the plaza"),
    ),
  );
}

export function joinButton(instance: string, text = "Join in Banger"): HTMLElement {
  const href = joinLink(instance);
  return h(
    "a",
    {
      class: "btn-play small",
      href,
      "aria-label": `${text} (opens the Banger app)`,
      onclick: (e: Event) => {
        e.preventDefault();
        launch(PLAZA_GAME, { link: href, onMissing: () => navigate(downloadPath()) });
      },
    },
    h("span", { "aria-hidden": "true", class: "tri" }, "▶"),
    text,
  );
}

function eventCard(e: PlazaEvent, me: Me | null, moderator: boolean, reload: () => Promise<void>): HTMLElement {
  const minor = me?.ageBand === "13-17";
  const size = sizeText(e);
  const feature = moderator
    ? h(
        "button",
        {
          type: "button",
          class: "btn small ghost",
          onclick: async (ev: Event) => {
            const b = ev.currentTarget as HTMLButtonElement;
            b.disabled = true;
            try {
              await featureEvent(e.id, !e.featured);
              toast(e.featured ? "No longer featured" : "Featured");
              await reload();
            } catch (x) {
              toast(eventError(x));
              b.disabled = false;
            }
          },
        },
        e.featured ? "Unfeature" : "Feature",
      )
    : null;
  return h(
    "li",
    { class: `event${e.featured ? " featured" : ""}${e.status === "live" ? " live" : ""}`, id: e.id },
    h(
      "div",
      { class: "event-when" },
      h("strong", null, e.status === "live" ? "Live now" : fmtTime(e.at)),
      e.status === "live" && e.startedAt ? h("span", { class: "dim small" }, `since ${fmtTime(e.startedAt)}`) : null,
    ),
    h(
      "div",
      { class: "event-what" },
      h(
        "div",
        { class: "event-tags" },
        h("span", { class: "badge" }, KIND_LABEL[e.kind] ?? e.kind),
        e.featured ? h("span", { class: "badge ok" }, "Featured") : null,
        e.status === "live" ? h("span", { class: "badge bad" }, "Live") : null,
      ),
      h("h3", null, e.title),
      h("p", { class: "dim small" }, e.host.id ? `Hosted by ${e.host.name}` : "Host hidden", size ? ` · ${size}` : null),
      h("p", { class: "small" }, KIND_BLURB[e.kind] ?? ""),
      minor && e.kind === "concert" && !e.featured ? h("p", { class: "note small" }, "Concert audio plays for under-18 accounts only at featured concerts. You can still join, watch and react.") : null,
    ),
    h("div", { class: "event-actions" }, joinButton(e.instance), feature),
  );
}

export function hostPanel(me: Me | null, reload: () => Promise<void>): HTMLElement {
  const head = h("h2", null, "Host an event");
  if (!me)
    return h(
      "section",
      { class: "panel host-panel" },
      head,
      h("p", null, "Anyone 18 or older can host a pitch night, a concert or a meetup on the plaza."),
      link(`/signin?next=${encodeURIComponent("/events")}`, { class: "btn" }, "Sign in to host"),
    );
  if (me.ageBand !== "18+")
    return h("section", { class: "panel host-panel" }, head, h("p", { class: "dim" }, "Hosting events needs an 18+ account. You can join any event and react from the crowd."));

  const title = h("input", { id: "ev-title", maxlength: 80, required: true, placeholder: "Friday pitch night", autocomplete: "off" }) as HTMLInputElement;
  const kind = h(
    "select",
    { id: "ev-kind" },
    (Object.keys(KIND_LABEL) as EventKind[]).map((k) => h("option", { value: k }, KIND_LABEL[k])),
  ) as HTMLSelectElement;
  const soon = Math.ceil((Date.now() + 3_600_000) / 1_800_000) * 1_800_000; // the next half hour, an hour out
  const when = h("input", { id: "ev-when", type: "datetime-local", required: true, value: toLocalInput(soon), min: toLocalInput(Date.now()), max: toLocalInput(Date.now() + 89 * 86_400_000) }) as HTMLInputElement;
  const slots = h("input", { id: "ev-slots", type: "number", min: 1, max: 20, value: 5, inputmode: "numeric" }) as HTMLInputElement;
  const circles = h("input", { id: "ev-circles", type: "number", min: 1, max: 8, value: 3, inputmode: "numeric" }) as HTMLInputElement;
  const seats = h("input", { id: "ev-seats", type: "number", min: 3, max: 12, value: 8, inputmode: "numeric" }) as HTMLInputElement;
  const slotsLabel = h("label", { for: "ev-slots" }, "Pitch slots");
  const slotsField = h("div", { class: "field" }, slotsLabel, slots);
  const meetupFields = h("div", { class: "field-row", hidden: true }, h("div", { class: "field" }, h("label", { for: "ev-circles" }, "Circles"), circles), h("div", { class: "field" }, h("label", { for: "ev-seats" }, "Seats per circle"), seats));
  const blurb = h("p", { class: "dim small" }, KIND_BLURB.pitch);
  const msg = h("div", { "aria-live": "polite" });
  const sync = () => {
    const k = kind.value as EventKind;
    slotsField.hidden = k === "meetup";
    meetupFields.hidden = k !== "meetup";
    slotsLabel.textContent = k === "concert" ? "Acts" : "Pitch slots";
    blurb.textContent = KIND_BLURB[k];
  };
  kind.addEventListener("change", sync);
  const submit = h("button", { class: "btn", type: "submit" }, "Schedule");
  const form = h(
    "form",
    {
      class: "stack",
      onsubmit: async (e: Event) => {
        e.preventDefault();
        const draft: Draft = { title: title.value, kind: kind.value as EventKind, at: parseLocal(when.value), slots: Number(slots.value), circles: Number(circles.value), seats: Number(seats.value) };
        const problem = checkDraft(draft);
        if (problem) return clear(msg, h("p", { class: "error", role: "alert" }, problem));
        submit.setAttribute("disabled", "");
        clear(msg, h("p", { class: "dim" }, "Scheduling…"));
        try {
          await scheduleEvent(draft, nameOf(me));
          toast(`Scheduled for ${fmtTime(draft.at!)}. It's on the calendar.`);
          await reload(); // draws a fresh form
        } catch (x) {
          clear(msg, h("p", { class: "error", role: "alert" }, eventError(x)));
        } finally {
          submit.removeAttribute("disabled");
        }
      },
    },
    h("div", { class: "field" }, h("label", { for: "ev-title" }, "Title"), title),
    h("div", { class: "field-row" }, h("div", { class: "field" }, h("label", { for: "ev-kind" }, "Type"), kind), h("div", { class: "field" }, h("label", { for: "ev-when" }, `Starts (${viewerZone().replace(/_/g, " ")})`), when)),
    blurb,
    slotsField,
    meetupFields,
    submit,
    msg,
  );
  sync();
  return h(
    "section",
    { class: "panel host-panel" },
    head,
    h("p", null, "Pick a time and the plaza puts it on the calendar. You run it from the stage in the Banger app: bring people up, start and end it."),
    h("p", { class: "dim small" }, "Keep it kind: the plaza is 13+. Moderators can feature events they'd like more people to see."),
    form,
  );
}
